import React from 'react';
import PropTypes from 'prop-types';
import DataTable from 'react-data-table-component';
import { formatForDisplay } from '../../../../utils/dateformatpickr';

// Inner table rendered inside each expandable Evaluation/Attachment section
// (rule §Tab Evaluation 2 / §Tab Implementation b). Columns: File Name /
// Reference Document / Effective Date / Action, plus a "+" Add button.
// Documents live in the parent shell keyed by evaluationId; upload/delete are
// handled there — this stays presentational. In read-only mode the Action column
// offers Download (for already-saved docs with an `existingId`) instead of Delete.
// Client-side sortable on File Name / Reference Document / Effective Date.
const DocumentSectionTable = ({
  documents,
  onAdd,
  onDelete,
  onDownload,
  disabled,
  readOnly,
}) => {
  const actionColumn = readOnly
    ? {
        name: 'Action',
        width: '90px',
        center: true,
        cell: row =>
          row.existingId ? (
            <button
              type='button'
              className='btn btn-soft-info btn-sm btn-icon'
              title='Download'
              onClick={() => onDownload && onDownload(row)}
            >
              <i className='ri-download-2-line align-bottom'></i>
            </button>
          ) : null,
      }
    : {
        name: 'Action',
        width: '90px',
        center: true,
        cell: row => (
          <button
            type='button'
            className='btn btn-soft-danger btn-sm btn-icon'
            title='Delete'
            disabled={disabled}
            onClick={() => onDelete(row)}
          >
            <i className='ri-delete-bin-line align-bottom'></i>
          </button>
        ),
      };

  const columns = [
    {
      name: 'File Name',
      selector: row => row.fileName,
      sortable: true,
      wrap: true,
      grow: 2,
      // Staged-but-not-yet-uploaded rows (Create wizard deferred flow) carry
      // status='pending' — flag them so the user can tell them from saved docs.
      cell: row => (
        <span>
          {row.fileName}
          {row.status === 'pending' && (
            <span className='badge bg-warning-subtle text-warning ms-2'>
              Pending upload
            </span>
          )}
        </span>
      ),
    },
    {
      name: 'Reference Document',
      selector: row => row.refDocument,
      sortable: true,
      wrap: true,
    },
    {
      name: 'Effective Date',
      selector: row => row.effDate,
      sortable: true,
      format: row => formatForDisplay(row.effDate, { includeTime: false }),
    },
    // Read-only → Download (saved docs only); otherwise → Delete.
    actionColumn,
  ];

  return (
    <div className='p-3 bg-light'>
      {!readOnly && (
        <div className='mb-2'>
          <button
            type='button'
            className='btn btn-soft-primary btn-sm'
            disabled={disabled}
            onClick={onAdd}
          >
            <i className='ri-add-line align-bottom me-1'></i>Add
          </button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={documents}
        dense
        striped
        noDataComponent={
          <div className='py-3 text-muted'>No records to display</div>
        }
      />
    </div>
  );
};

DocumentSectionTable.propTypes = {
  documents: PropTypes.array,
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  onDownload: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

DocumentSectionTable.defaultProps = {
  documents: [],
  readOnly: false,
};

export default DocumentSectionTable;
